/**
 * useSolomonAgent Hook
 *
 * Handles SSE streaming for the Solomon Claude Agent.
 * Provides real-time text streaming, tool use events, and session management.
 *
 * Supports two streaming formats:
 * - 'legacy': Original Solomon event format (default)
 * - 'agui': AG-UI protocol format for CopilotKit-style interactions
 *
 * Decomposed into focused sub-hooks:
 * - sseHelpers: pure parsing/ID generation
 * - useSSEConnection: fetch + stream reading
 * - useLegacyEventHandler: legacy event processing
 * - useAGUIEventHandler: AG-UI event processing
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { SERVER_BASE_URI } from '@pray/shared/config';

import { generateMessageId, fileToDataUrl } from './sseHelpers';
import type { SolomonStreamEvent } from './sseHelpers';
import { useAGUIEventHandler } from './useAGUIEventHandler';
import { useLegacyEventHandler } from './useLegacyEventHandler';
import type { UseMessageStoreReturn } from './useMessageStore';
import { useSSEConnection } from './useSSEConnection';
import type {
  AGUIEvent,
  UIActionPlanPayload,
  UIActionStartPayload,
  UIActionCompletePayload,
  UIActionErrorPayload,
} from '../../types/agui';
import type { SolomonAction } from '../../types/solomon';

// ============================================================================
// Re-exports (preserve external API)
// ============================================================================

export type { SolomonStreamEvent, SolomonStreamEventType } from './sseHelpers';

export type UIActionEvent =
  | { type: 'UI_ACTION_PLAN'; payload: UIActionPlanPayload }
  | { type: 'UI_ACTION_START'; payload: UIActionStartPayload }
  | { type: 'UI_ACTION_COMPLETE'; payload: UIActionCompletePayload }
  | { type: 'UI_ACTION_ERROR'; payload: UIActionErrorPayload };

export const StreamFormat = {
  LEGACY: 'legacy',
  AGUI: 'agui',
} as const;

export type StreamFormatType = (typeof StreamFormat)[keyof typeof StreamFormat];

export type SolomonAgentMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  toolUses?: Array<{
    tool: string;
    toolCallId: string;
    input?: Record<string, unknown>;
    result?: unknown;
    isError?: boolean;
  }>;
  timestamp: Date;
};

export type StreamState = {
  isStreaming: boolean;
  isThinking: boolean;
  currentResponse: string;
  currentThinking: string;
  activeToolUse: {
    tool: string;
    toolCallId: string;
    input?: Record<string, unknown>;
  } | null;
  sessionId: string | null;
  error: Error | null;
};

export type SolomonAgentState = StreamState & {
  messages: SolomonAgentMessage[];
};

export type UseSolomonAgentOptions = {
  artistId: string;
  context: string;
  messageStore?: UseMessageStoreReturn;
  streamFormat?: StreamFormatType;
  onEvent?: (event: SolomonStreamEvent) => void;
  onAGUIEvent?: (event: AGUIEvent) => void;
  onUIActionEvent?: (event: UIActionEvent) => void;
  onMessage?: (message: SolomonAgentMessage) => void;
  onError?: (error: Error) => void;
  onApprovalRequired?: (action: SolomonAction) => void;
};

export type UseSolomonAgentReturn = SolomonAgentState & {
  sendMessage: (prompt: string, metadata?: Record<string, unknown>) => Promise<void>;
  sendMessageWithImage: (prompt: string, image: File) => Promise<void>;
  stopStream: () => void;
  clearMessages: () => void;
  clearSession: () => void;
};

// ============================================================================
// Hook
// ============================================================================

export function useSolomonAgent({
  artistId,
  context,
  messageStore,
  streamFormat = StreamFormat.LEGACY,
  onEvent,
  onAGUIEvent,
  onUIActionEvent,
  onMessage,
  onError,
  onApprovalRequired,
}: UseSolomonAgentOptions): UseSolomonAgentReturn {
  const useExternalStore = !!messageStore;
  const useAGUIFormat = streamFormat === StreamFormat.AGUI;

  // Stable refs for option callbacks to avoid re-creating downstream hooks
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const messageStoreRef = useRef(messageStore);
  messageStoreRef.current = messageStore;

  const [streamState, setStreamState] = useState<StreamState>({
    isStreaming: false,
    isThinking: false,
    currentResponse: '',
    currentThinking: '',
    activeToolUse: null,
    sessionId: null,
    error: null,
  });

  const [internalMessages, setInternalMessages] = useState<SolomonAgentMessage[]>([]);

  const messages = useMemo(() => {
    if (useExternalStore && messageStore) {
      return messageStore.messages.filter((m) => m.role !== 'system') as SolomonAgentMessage[];
    }
    return internalMessages;
  }, [useExternalStore, messageStore, internalMessages]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const useExternalStoreRef = useRef(useExternalStore);
  useExternalStoreRef.current = useExternalStore;

  useEffect(() => {
    sessionIdRef.current = streamState.sessionId;
  }, [streamState.sessionId]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const { connect } = useSSEConnection();

  // Shared callback: update stream state (stable — no deps)
  const onStreamStateUpdate = useCallback((update: Partial<StreamState>) => {
    setStreamState((prev) => ({ ...prev, ...update }));
  }, []);

  // Shared callback: store a completed message (stable — uses refs)
  const onStoreMessage = useCallback(
    (content: string, thinking: string | undefined, toolUses: SolomonAgentMessage['toolUses']) => {
      if (useExternalStoreRef.current && messageStoreRef.current) {
        messageStoreRef.current.addAssistantMessage(content, { thinking, toolUses });
      } else {
        const msg: SolomonAgentMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content,
          thinking,
          toolUses,
          timestamp: new Date(),
        };
        setInternalMessages((prev) => [...prev, msg]);
      }
    },
    []
  );

  const legacyHandler = useLegacyEventHandler({
    onStreamStateUpdate,
    onMessageComplete: (msg) => onMessage?.(msg),
    onStoreMessage,
    onEvent,
    onError,
    onApprovalRequired,
  });

  const aguiHandler = useAGUIEventHandler({
    onStreamStateUpdate,
    onMessageComplete: (msg) => onMessage?.(msg),
    onStoreMessage,
    onAGUIEvent,
    onUIActionEvent,
    onError,
  });

  const sendMessage = useCallback(
    async (prompt: string, metadata?: Record<string, unknown>) => {
      if (!artistId || !context) {
        const error = new Error('Artist ID and context are required');
        setStreamState((prev) => ({ ...prev, error }));
        onErrorRef.current?.(error);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Add user message
      if (useExternalStoreRef.current && messageStoreRef.current) {
        messageStoreRef.current.addUserMessage(prompt);
      } else {
        setInternalMessages((prev) => [
          ...prev,
          { id: generateMessageId(), role: 'user', content: prompt, timestamp: new Date() },
        ]);
      }

      // Reset state
      setStreamState((prev) => ({
        ...prev,
        isStreaming: true,
        isThinking: true,
        error: null,
        currentResponse: '',
        currentThinking: '',
        activeToolUse: null,
      }));

      const handler = useAGUIFormat ? aguiHandler : legacyHandler;
      handler.reset();

      try {
        const url = `${SERVER_BASE_URI}/web/studio/artists/${artistId}/solomon/query`;
        const body = {
          prompt,
          context,
          ...(sessionIdRef.current && { sessionId: sessionIdRef.current }),
          metadata,
          streamFormat: useAGUIFormat ? StreamFormat.AGUI : StreamFormat.LEGACY,
        };

        await connect(url, body, abortControllerRef.current.signal, {
          onLine: handler.handleLine,
          onDone: handler.handleStreamDone,
          onError: (err) => {
            setStreamState((prev) => ({ ...prev, isStreaming: false, isThinking: false, error: err }));
            onErrorRef.current?.(err);
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          setStreamState((prev) => ({ ...prev, isStreaming: false, isThinking: false }));
          return;
        }
        const error = err instanceof Error ? err : new Error(String(err));
        setStreamState((prev) => ({ ...prev, isStreaming: false, isThinking: false, error }));
        onErrorRef.current?.(error);
      }
    },
    [artistId, context, useAGUIFormat, aguiHandler, legacyHandler, connect]
  );

  const sendMessageWithImage = useCallback(
    async (prompt: string, image: File) => {
      try {
        const imageDataUrl = await fileToDataUrl(image);
        const enhancedPrompt = `[User attached an image: ${image.name}]\n\n${prompt}`;
        await sendMessage(enhancedPrompt, {
          attachedImage: {
            dataUrl: imageDataUrl,
            filename: image.name,
            mimeType: image.type,
            size: image.size,
          },
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to process image');
        setStreamState((prev) => ({ ...prev, error }));
        onErrorRef.current?.(error);
      }
    },
    [sendMessage]
  );

  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setStreamState((prev) => ({ ...prev, isStreaming: false, isThinking: false }));
  }, []);

  const clearMessages = useCallback(() => {
    if (useExternalStoreRef.current && messageStoreRef.current) {
      messageStoreRef.current.clear();
    } else {
      setInternalMessages([]);
    }
    setStreamState((prev) => ({ ...prev, currentResponse: '', currentThinking: '', error: null }));
  }, []);

  const clearSession = useCallback(() => {
    if (useExternalStoreRef.current && messageStoreRef.current) {
      messageStoreRef.current.clear();
    } else {
      setInternalMessages([]);
    }
    setStreamState((prev) => ({ ...prev, currentResponse: '', currentThinking: '', sessionId: null, error: null }));
  }, []);

  return {
    ...streamState,
    messages,
    sendMessage,
    sendMessageWithImage,
    stopStream,
    clearMessages,
    clearSession,
  };
}

export default useSolomonAgent;
